import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { Download, Trash2, Upload } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { taskService, type TaskAttachment } from '@/lib/services/taskService'
import { formatFileSize } from '@/lib/utils/parentTicketAttachments'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type Props = {
  taskId: number
  isDisabled?: boolean
}

export default function TaskAttachmentsList({ taskId, isDisabled = false }: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachments, setAttachments] = useState<TaskAttachment[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const loadAttachments = useCallback(async () => {
    setLoading(true)
    try {
      const rows = await taskService.listAttachments(taskId)
      setAttachments(rows)
    } catch (err) {
      showError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [taskId, showError])

  useEffect(() => {
    void loadAttachments()
  }, [loadAttachments])

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length || isDisabled) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        await taskService.uploadAttachment(taskId, file)
      }
      showSuccess(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded')
      await loadAttachments()
    } catch (err) {
      showError(err as Error)
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDelete = async (attachmentId: number) => {
    if (isDisabled) return
    setDeletingId(attachmentId)
    try {
      await taskService.deleteAttachment(taskId, attachmentId)
      showSuccess('Attachment removed')
      setAttachments((prev) => prev.filter((item) => item.id !== attachmentId))
    } catch (err) {
      showError(err as Error)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDownload = async (attachment: TaskAttachment) => {
    try {
      const blob = await taskService.fetchAttachmentFile(taskId, attachment.id)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = attachment.original_filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      showError(err as Error)
    }
  }

  return (
    <Box>
      <HStack justify="space-between" mb={3}>
        <Text fontSize="sm" fontWeight="600" color={colors.textSecondary}>
          Attachments
        </Text>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Upload size={14} />}
          onClick={() => fileInputRef.current?.click()}
          isLoading={uploading}
          isDisabled={isDisabled}
        >
          Upload
        </Button>
        <Input
          ref={fileInputRef}
          type="file"
          multiple
          display="none"
          onChange={(e) => void handleUpload(e.target.files)}
        />
      </HStack>

      {loading ? (
        <HStack py={4} justify="center">
          <Spinner size="sm" />
          <Text fontSize="sm" color={colors.textSecondary}>
            Loading attachments…
          </Text>
        </HStack>
      ) : attachments.length === 0 ? (
        <Text fontSize="sm" color={colors.textSecondary}>
          No attachments on this task.
        </Text>
      ) : (
        <VStack align="stretch" spacing={2}>
          {attachments.map((attachment) => (
            <HStack
              key={attachment.id}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor={colors.borderLight}
              bg={colors.cardBg}
              justify="space-between"
            >
              <Box minW={0}>
                <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                  {attachment.original_filename}
                </Text>
                <Text fontSize="xs" color={colors.textSecondary}>
                  {formatFileSize(attachment.size_bytes ?? null)}
                  {attachment.uploader_name ? ` · ${attachment.uploader_name}` : ''}
                </Text>
              </Box>
              <HStack spacing={1} flexShrink={0}>
                <Tooltip label="Download">
                  <IconButton
                    aria-label="Download attachment"
                    size="sm"
                    variant="ghost"
                    icon={<Download size={16} />}
                    onClick={() => void handleDownload(attachment)}
                  />
                </Tooltip>
                <Tooltip label="Remove">
                  <IconButton
                    aria-label="Delete attachment"
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<Trash2 size={16} />}
                    onClick={() => void handleDelete(attachment.id)}
                    isLoading={deletingId === attachment.id}
                    isDisabled={isDisabled}
                  />
                </Tooltip>
              </HStack>
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  )
}
